import React from 'react';
import { PropertyGroup } from '../PropertyGroup';
import { PropertyNumber } from '../property-types/PropertyNumber';
import { PropertyCheckbox } from '../property-types/PropertyCheckbox';

interface TransformData {
  x: number;
  y: number;
  scaleX: number;
  scaleY: number;
  rotation: number;
}

interface TransformGroupProps {
  transform: TransformData;
  onTransformChange: (property: keyof TransformData, value: number) => void;
  onLockSizeChange?: (locked: boolean) => void;
}

export const TransformGroup: React.FC<TransformGroupProps> = ({
  transform,
  onTransformChange,
  onLockSizeChange
}) => {
  const [lockSize, setLockSize] = React.useState(true);

  const handleLockSizeChange = (locked: boolean) => {
    setLockSize(locked);
    if (onLockSizeChange) {
      onLockSizeChange(locked);
    }
  };

  // Handle scale changes with aspect ratio locking
  const handleScaleXChange = (value: number) => {
    onTransformChange('scaleX', value);
    if (lockSize) {
      // Maintain aspect ratio
      onTransformChange('scaleY', value);
    }
  };

  const handleScaleYChange = (value: number) => {
    onTransformChange('scaleY', value);
    if (lockSize) {
      // Maintain aspect ratio
      onTransformChange('scaleX', value);
    }
  };

  return (
    <PropertyGroup title="Transform">
      {/* Move */}
      <div className="flex items-center gap-2 py-0.5 mt-1">
        <span className="text-gray-900 dark:text-white text-sm font-medium w-[30%] flex-shrink-0">Move</span>
        <div className="flex flex-1 gap-2">
          <PropertyNumber
            label="X"
            value={transform.x}
            onChange={(value) => onTransformChange('x', value)}
          />
          <PropertyNumber
            label="Y"
            value={transform.y}
            onChange={(value) => onTransformChange('y', value)}
          />
        </div>
      </div>

      {/* Rotate */}
      <div className="flex items-center gap-2 py-0.5">
        <span className="text-gray-900 dark:text-white text-sm font-medium w-[30%] flex-shrink-0">Rotate</span>
        <div className="flex flex-1 gap-2">
          <PropertyNumber
            label="Z"
            value={transform.rotation}
            onChange={(value) => onTransformChange('rotation', value)}
            suffix="°"
            step={1}
          />
        </div>
      </div>

      {/* Size */}
      <div className="space-y-0.5">
        <div className="flex items-center gap-2 py-0.5">
          <span className="text-gray-900 dark:text-white text-sm font-medium w-[30%] flex-shrink-0">Size</span>
          <div className="flex flex-1 gap-2">
            <PropertyNumber
              label="X"
              value={transform.scaleX}
              onChange={handleScaleXChange}
              step={0.1}
              min={0.1}
              max={5}
            />
            <PropertyNumber
              label="Y"
              value={transform.scaleY}
              onChange={handleScaleYChange}
              step={0.1}
              min={0.1}
              max={5}
            />
          </div>
        </div>
        
        <PropertyCheckbox
          label="Lock Size"
          checked={lockSize}
          onChange={handleLockSizeChange}
        />
      </div>
    </PropertyGroup>
  );
};

